import React, { useEffect, useState } from "react";
import { fetchResources, createResource, updateResource, deleteResource } from "../services/api";
import { useT } from "../i18n/useT";

// Services & Memberships — scheduler resources (SM5a). Therapists and treatment
// rooms that appointments are booked against. Setup only; the calendar is SM5c.
const S = {
  en: {
    title: "Resources", subtitle: "Therapists and rooms the scheduler books into",
    add: "Add resource", name: "Name", kind: "Type", staff: "Therapist / staff", room: "Room",
    area: "Area (optional)", any: "Any area", spa: "Spa", gym: "Gym", pool: "Pool", salon: "Salon", activity: "Activities",
    save: "Save", none: "No resources yet — add therapists and rooms above.",
    del: "Delete", del_q: "Delete this resource?", inactive: "inactive", edit: "Edit", cancel: "Cancel",
  },
  am: {
    title: "መርጃዎች", subtitle: "መርሐግብር የሚይዝባቸው ቴራፒስቶችና ክፍሎች",
    add: "መርጃ ጨምር", name: "ስም", kind: "አይነት", staff: "ቴራፒስት / ሰራተኛ", room: "ክፍል",
    area: "ዘርፍ (አማራጭ)", any: "ማንኛውም ዘርፍ", spa: "ስፓ", gym: "ጂም", pool: "መዋኛ", salon: "ሳሎን", activity: "እንቅስቃሴዎች",
    save: "አስቀምጥ", none: "እስካሁን መርጃ የለም — ከላይ ይጨምሩ።",
    del: "ሰርዝ", del_q: "ይህን መርጃ ይሰረዝ?", inactive: "ገቢር አይደለም", edit: "አስተካክል", cancel: "ሰርዝ",
  },
  om: {
    title: "Qabeenyawwan", subtitle: "Terapistootaa fi kutaawwan sagantaan qabatu",
    add: "Qabeenya dabali", name: "Maqaa", kind: "Gosa", staff: "Terapistii / hojjetaa", room: "Kutaa",
    area: "Naannoo (filannoo)", any: "Naannoo kamiyyuu", spa: "Spaa", gym: "Jiimii", pool: "Daakaa", salon: "Saaloonii", activity: "Sochiiwwan",
    save: "Olkaa'i", none: "Hanga ammaa qabeenyi hin jiru — olitti dabali.",
    del: "Haqi", del_q: "Qabeenya kana haquu?", inactive: "hin hojjetu", edit: "Sirreessi", cancel: "Haqi",
  },
};

const AREAS = ["spa", "gym", "pool", "salon", "activity"];
const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const inp = { padding: "9px 10px", border: "1px solid #d8dee2", borderRadius: 8, fontSize: 14 };
const EMPTY = { name: "", kind: "staff", area: "" };

export default function ServiceResourcesPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => fetchResources().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const reset = () => { setEditId(null); setForm(EMPTY); };
  const startEdit = (r) => { setEditId(r.id); setForm({ name: r.name, kind: r.kind, area: r.area || "" }); window.scrollTo(0, 0); };
  const save = () => {
    if (!form.name.trim()) return;
    setBusy(true);
    const body = { name: form.name.trim(), kind: form.kind, area: form.area || null };
    (editId ? updateResource(editId, body) : createResource(body))
      .then(() => { reset(); load(); }).catch(() => {}).finally(() => setBusy(false));
  };
  const remove = (r) => { if (window.confirm(t("del_q"))) deleteResource(r.id).then(load).catch(load); };

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      <div style={{ ...CARD, padding: 16, marginTop: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>{editId ? t("edit") : t("add")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          <input style={inp} placeholder={t("name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select style={inp} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="staff">{t("staff")}</option>
            <option value="room">{t("room")}</option>
          </select>
          <select style={inp} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}>
            <option value="">{t("any")}</option>
            {AREAS.map((a) => <option key={a} value={a}>{t(a)}</option>)}
          </select>
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <button className="btn primary small" disabled={busy || !form.name.trim()} onClick={save}>{t("save")}</button>
          {editId && <button className="btn secondary small" onClick={reset}>{t("cancel")}</button>}
        </div>
      </div>

      {rows == null ? <p className="muted" style={{ marginTop: 16 }}>…</p> : rows.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 12, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((r) => (
            <div key={r.id} style={{ ...CARD, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 20 }}>{r.kind === "room" ? "🚪" : "🧖"}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{r.name}{!r.active && <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}> · {t("inactive")}</span>}</div>
                <div className="muted" style={{ fontSize: 12.5 }}>{t(r.kind)}{r.area ? ` · ${t(r.area)}` : ""}</div>
              </div>
              <button className="btn secondary small" onClick={() => startEdit(r)}>{t("edit")}</button>
              <button onClick={() => remove(r)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t("del")}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
